import React from "react";
import { Card, CardBody, Row, Col, Badge, Button } from "reactstrap";
import { DIAS_SEMANA } from "./horariosClubesComponentConstants";
import { LoadingStates } from "./horariosClubesComponentTypes";

interface Props {
  config: any;
  onEditarMantenimientos: (id: string) => void;
  onEditarClubes: (id: string) => void;
  onEliminar: (id: string) => void;
  onDiaClick: (dia: string, configId: string) => void;
  loadingStates: LoadingStates;
  isAnyOperationInProgress: () => boolean;
}

const ConfiguracionCard: React.FC<Props> = ({
  config,
  onEditarMantenimientos,
  onEditarClubes,
  onEliminar,
  onDiaClick,
  loadingStates,
  isAnyOperationInProgress
}) => {
  const getAdvertencia = () => {
    return (
      <div className="text-center mt-3">
        <small className="text-light">
          {isAnyOperationInProgress() &&
            "Espera a que termine la operación actual"}
        </small>
      </div>
    );
  };

  const getDiaTooltip = (dia: any, horario: any) => {
    if (isAnyOperationInProgress()) {
      return "Operación en progreso, espera...";
    }

    if (horario && horario.activo) {
      return `${dia.nombre}: ${horario.horaInicio} - ${horario.horaFin} (Click para editar)`;
    }

    return `Configurar horario para ${dia.nombre}`;
  };

  return (
    <Card className="mb-4 shadow-sm">
      <CardBody style={{ backgroundColor: "#313038", color: "white" }}>
        <Row className="align-items-center">
          {/* Columna de Mantenimiento */}
          <Col md={3} className="border-right">
            <div className="text-center">
              <Badge color="warning" className="mb-3">
                Mantenimientos Asignados ({config.mantenimientos.length})
              </Badge>
              <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                {config.mantenimientos.slice(0, 3).map((mant: any) => (
                  <div key={mant.id}>
                    <small className="text-warning d-block">
                      {mant.nombre}
                    </small>
                  </div>
                ))}
                {config.mantenimientos.length > 3 && (
                  <small className="text-muted">
                    +{config.mantenimientos.length - 3} más...
                  </small>
                )}
              </div>
              <div className="mt-2">
                <Button
                  color="outline-warning"
                  size="sm"
                  disabled={isAnyOperationInProgress()}
                  style={{
                    cursor: isAnyOperationInProgress()
                      ? "not-allowed"
                      : "pointer",
                    opacity: isAnyOperationInProgress() ? 0.6 : 1
                  }}
                  onClick={() =>
                    onEditarMantenimientos(config.configuracionId.toString())
                  }
                  title="Editar mantenimientos"
                >
                  <i className="fa fa-edit"></i> Editar
                </Button>
              </div>
            </div>
          </Col>

          {/* Columna de Horarios */}
          <Col md={6} className="border-right">
            <div className="text-center">
              <Badge color="danger" className="mb-3">
                Horarios Asignados
              </Badge>
              <div className="d-flex justify-content-center flex-wrap">
                {DIAS_SEMANA.map(dia => {
                  const horario = config.horarios.find(
                    (h: any) => h.dia === dia.key
                  );
                  const isActive = horario ? horario.activo : false;

                  return (
                    <div
                      key={dia.key}
                      className="mx-2 mb-2 d-flex flex-column align-items-center cursor-pointer"
                      onClick={() => {
                        if (!isAnyOperationInProgress()) {
                          onDiaClick(
                            dia.key,
                            config.configuracionId.toString()
                          );
                        }
                      }}
                      style={{
                        cursor: isAnyOperationInProgress()
                          ? "not-allowed"
                          : "pointer",
                        opacity: isAnyOperationInProgress() ? 0.6 : 1
                      }}
                      title={getDiaTooltip(dia, horario)}
                    >
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center ${
                          isActive
                            ? "bg-danger text-white"
                            : "bg-light text-muted"
                        }`}
                        style={{
                          width: "40px",
                          height: "40px",
                          border: isActive
                            ? "2px solid #ce072c"
                            : "2px solid #dee2e6",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <strong>{dia.label}</strong>
                      </div>
                      {isActive && (
                        <div className="text-center mt-1">
                          <small
                            className="text-white"
                            style={{ fontSize: "10px" }}
                          >
                            {horario ? horario.horaInicio : ""} -{" "}
                            {horario ? horario.horaFin : ""}
                          </small>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {getAdvertencia()}
            </div>
          </Col>

          {/* Columna de Club */}
          <Col md={2} className="border-right">
            <div className="text-center">
              <Badge color="success" className="mb-3">
                Clubes Asignados ({config.clubes.length})
              </Badge>
              <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                {config.clubes.slice(0, 3).map((club: any) => (
                  <div key={club.id}>
                    <small className="text-success d-block">
                      {club.nombre}
                    </small>
                  </div>
                ))}
                {config.clubes.length > 3 && (
                  <small className="text-muted">
                    +{config.clubes.length - 3} más...
                  </small>
                )}
              </div>
              <div className="mt-2">
                <Button
                  color="outline-success"
                  size="sm"
                  disabled={isAnyOperationInProgress()}
                  style={{
                    cursor: isAnyOperationInProgress()
                      ? "not-allowed"
                      : "pointer",
                    opacity: isAnyOperationInProgress() ? 0.6 : 1
                  }}
                  onClick={() =>
                    onEditarClubes(config.configuracionId.toString())
                  }
                  title="Editar clubes"
                >
                  <i className="fa fa-edit"></i> Editar
                </Button>
              </div>
            </div>
          </Col>

          {/* Columna de Acciones */}
          <Col md={1}>
            <div className="text-center">
              <Badge color="danger" className="mb-3">
                Acciones
              </Badge>
              <div className="mt-2">
                <Button
                  color="danger"
                  size="sm"
                  disabled={isAnyOperationInProgress()}
                  style={{
                    cursor: isAnyOperationInProgress()
                      ? "not-allowed"
                      : "pointer",
                    opacity: isAnyOperationInProgress() ? 0.6 : 1
                  }}
                  outline
                  onClick={() => onEliminar(config.configuracionId.toString())}
                  title="Eliminar configuración"
                >
                  ✕
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default ConfiguracionCard;
